import { type RefObject, useEffect, useRef } from 'react'

import { useInView } from 'motion/react'

import { track } from '@/api/tracking'

/** Time a section must stay on screen to count as seen, so a smooth scroll to #form does not fire it. */
export const SECTION_VIEW_DWELL_MS = 1000

/**
 * Fires `eventName` once, after the section has stayed at least 20 % on screen for {@link SECTION_VIEW_DWELL_MS}.
 * Properties are read when the event fires, so callers can pass an inline object.
 */
export function useSectionViewTrack(
    ref: RefObject<Element | null>,
    eventName: string,
    properties?: Record<string, unknown>,
    enabled = true,
) {
    const isInView = useInView(ref, { amount: 0.2 })
    const hasTracked = useRef(false)
    const propertiesRef = useRef(properties)
    propertiesRef.current = properties

    useEffect(() => {
        if (!enabled || !isInView || hasTracked.current) return
        const timer = window.setTimeout(() => {
            hasTracked.current = true
            track(eventName, { ...propertiesRef.current, dwell_ms: SECTION_VIEW_DWELL_MS })
        }, SECTION_VIEW_DWELL_MS)
        return () => window.clearTimeout(timer)
    }, [enabled, eventName, isInView])
}
